import { Controller } from "@hotwired/stimulus"
import * as Y from "yjs"
import { CableProvider } from "../collab/cable_provider"
import { TextBinding } from "../collab/text_binding"
import { hueToHex, renderPeers } from "../collab/presence"

// A handbook page's edit form, with other editors live in it (Yjs over ActionCable, see CollabChannel): the title,
// the lead and the Markdown body are shared fields, each person's cursor shows in them, and what you see is what the
// others see as it is typed. The form is still saved with its Save button (a handbook page is saved on purpose, each
// save a revision in its history); the fields simply hold the merged text, so nobody saves over anybody. If the room
// cannot be reached the form is the plain form it always was.
const FIELDS = ["title", "lead", "body"]
const JOIN_TIMEOUT_MS = 3000

export default class extends Controller {
    static targets = ["title", "lead", "body", "presence", "status"]
    static values = { kind: String, id: Number, user: Object }

    async connect() {
        this._closed = false
        const joined = await this._join()
        if (this._closed) return this._leave(joined)

        this._joined = joined
        if (!joined) return

        this._bindings = FIELDS.filter((name) => this[`has${name[0].toUpperCase()}${name.slice(1)}Target`]).map((name) => {
            return new TextBinding(joined.ydoc.getText(name), this[`${name}Target`], { awareness: joined.provider.awareness, name })
        })
        joined.provider.awareness.on("change", () => this._peers())
        this._peers()
        // A Save from the live form is not a change from outside it: the server keeps the room open for the others.
        this._onSubmit = () => {
            const flag = document.createElement("input")
            flag.type = "hidden"
            flag.name = "live"
            flag.value = "1"
            this.element.append(flag)
        }
        this.element.addEventListener("submit", this._onSubmit)
        this._note("Editing together")
    }

    disconnect() {
        this._closed = true
        this.element.removeEventListener("submit", this._onSubmit)
        this._bindings?.forEach((binding) => binding.destroy())
        this._leave(this._joined)
    }

    async _join() {
        if (!this.userValue?.name) return null

        try {
            const ydoc = new Y.Doc()
            const user = { name: this.userValue.name, color: hueToHex(this.userValue.hue), avatar: this.userValue.avatar_url }
            const provider = new CableProvider(ydoc, { kind: this.kindValue, id: this.idValue, user, onReset: () => this._reload() })
            const state = await Promise.race([provider.ready, new Promise((resolve) => setTimeout(() => resolve(null), JOIN_TIMEOUT_MS))])
            if (!state?.writable) {
                provider.destroy()
                ydoc.destroy()
                return null
            }

            if (state.empty) {
                const start = new Y.Doc()
                FIELDS.forEach((name) => { if (this[`has${name[0].toUpperCase()}${name.slice(1)}Target`]) start.getText(name).insert(0, this[`${name}Target`].value) })
                await provider.seed(Y.encodeStateAsUpdate(start))
            }
            return { ydoc, provider }
        } catch (_error) {
            return null
        }
    }

    _leave(joined) {
        if (!joined) return

        joined.provider.destroy()
        joined.ydoc.destroy()
    }

    _peers() {
        renderPeers(this.hasPresenceTarget ? this.presenceTarget : null, this._joined.provider.awareness, this._joined.ydoc.clientID)
    }

    _note(text, problem = false) {
        if (!this.hasStatusTarget) return

        this.statusTarget.textContent = text
        this.statusTarget.classList.toggle("text-danger", problem)
    }

    // The saved page was changed by something other than this form (Claude over the playbook tools, a restore): what
    // is in the fields is out of date.
    _reload() {
        this._note("This page was just changed elsewhere. Reloading…", true)
        setTimeout(() => window.location.reload(), 1200)
    }
}
